import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { socketService } from '../../../services/socket';
import { useAuth } from '../../auth/contexts/AuthContext';
import { useTimecode } from '../../../hooks/useTimecode';

export interface ChatMessage {
    id: string;
    author: string;
    role: string;
    text: string;
    tc: string;
    createdAt: number;
    recipient?: string; // 'Global' or user name
    replyTo?: {
        messageId: string;
        author: string;
        text: string;
        tc: string;
    };
}

interface ChatContextType {
    messages: ChatMessage[];
    sendMessage: (text: string, recipient?: string, replyTo?: ChatMessage['replyTo']) => void;
    isChatOpen: boolean;
    setIsChatOpen: (open: boolean) => void;
    unreadCount: number;
    resetUnread: () => void;
    clearHistory: () => void;
    activeUsers: string[];
}

export const ChatContext = createContext<ChatContextType | undefined>(undefined);

export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { user } = useAuth();
    const { currentDisplayTC } = useTimecode();
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [activeUsers, setActiveUsers] = useState<string[]>([]);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    const isChatOpenRef = React.useRef(isChatOpen);
    useEffect(() => {
        isChatOpenRef.current = isChatOpen;
    }, [isChatOpen]);

    useEffect(() => {
        const handleInit = (data: any) => {
            if (data.chatMessages) {
                // Parse replyTo if it was stringified in DB (actually better to handle it in server/index.js)
                setMessages(data.chatMessages.map((m: any) => ({
                    ...m,
                    replyTo: typeof m.replyTo === 'string' ? JSON.parse(m.replyTo) : m.replyTo
                })));
            }
            if (data.activeLoggers) {
                const rawList = Array.isArray(data.activeLoggers) ? data.activeLoggers : Object.values(data.activeLoggers || {});
                setActiveUsers(rawList.map((u: any) => typeof u === 'object' ? u.name : String(u)));
            }
        };

        const handleActiveLoggersUpdated = (users: any) => {
            const rawList = Array.isArray(users) ? users : Object.values(users || {});
            setActiveUsers(rawList.map((u: any) => typeof u === 'object' ? u.name : String(u)));
        };

        const handleNewMessage = (msg: ChatMessage) => {
            setMessages(prev => {
                // Evitar duplicados por si acaso el server envía dos veces al mismo socket
                if (prev.some(m => m.id === msg.id)) return prev;
                const parsedMsg = {
                    ...msg,
                    replyTo: typeof msg.replyTo === 'string' ? JSON.parse(msg.replyTo) : msg.replyTo
                };
                return [...prev, parsedMsg];
            });

            if (!isChatOpenRef.current) {
                setUnreadCount(count => count + 1);
            }
        };

        const handleCleared = () => {
            setMessages([]);
            setUnreadCount(0);
        };

        socketService.on('init-data', handleInit);
        socketService.on('new-chat-message', handleNewMessage);
        socketService.on('chat-cleared', handleCleared);
        socketService.on('active-loggers-updated', handleActiveLoggersUpdated);

        return () => {
            socketService.off('init-data', handleInit);
            socketService.off('new-chat-message', handleNewMessage);
            socketService.off('chat-cleared', handleCleared);
            socketService.off('active-loggers-updated', handleActiveLoggersUpdated);
        };
    }, []);

    const sendMessage = useCallback((text: string, recipient: string = 'Global', replyTo?: ChatMessage['replyTo']) => {
        if (!user || !text.trim()) return;

        socketService.emit('send-chat-message', {
            author: user.name,
            role: user.role,
            text: text.trim(),
            tc: currentDisplayTC,
            recipient,
            replyTo
        });
    }, [user, currentDisplayTC]);

    const resetUnread = useCallback(() => {
        setUnreadCount(0);
    }, []);

    const clearHistory = useCallback(() => {
        socketService.emit('clear-chat');
    }, []);

    return (
        <ChatContext.Provider value={{
            messages,
            sendMessage,
            isChatOpen,
            setIsChatOpen,
            unreadCount,
            resetUnread,
            clearHistory,
            activeUsers
        }}>
            {children}
        </ChatContext.Provider>
    );
};

export const useChat = () => {
    const context = useContext(ChatContext);
    if (!context) throw new Error('useChat must be used within a ChatProvider');
    return context;
};
